import { Helmet } from "react-helmet"
import { toast } from "react-toastify"
import AsyncSelect from "react-select/async"
import { useNavigate } from "react-router-dom"
import { MenuButton } from "@headlessui/react"
import React, { useEffect, useState } from "react"
import { DatePicker, DatePickerInput, Theme } from "@carbon/react"

import { L0GXP } from "../../api/Registry"
import DropDown from "../../lib/fragments/DropDown"
import { getStandardRoute } from "../../utils/Helpers"
import HttpServices from "../../services/HttpServices"
import { LoadingXL } from "../../components/utils/LoadingXL"

export const LogExplorer = () => {
    const [state, setstate] = useState({
        posting: false,
        httpStatus: 200,
        status: 'pending',
        data: {
            events: [],
        },
        input: {
            date: [],
            to: null,
            from: null,
            events: [],
            users: [],
        }
    })

    const minDate = new Date();
    const currentDate = new Date();
    minDate.setDate(currentDate.getDate() - 90)

    const navigate = useNavigate()
    const pageTitle = "Log Xplorer"
    const isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches

    useEffect(() => {
        const fetchLogXplorerEvents = async () => {
            let { status, httpStatus } = state
            let events = []

            try {
                const response: any = await HttpServices.httpGet(L0GXP.EVENTS_LIST)
                httpStatus = response.status

                if (httpStatus === 200) {
                    const payload: any = response.data.payload
                    events = payload.events

                    status = 'fulfilled'
                } else {
                    status = 'rejected'
                }
            } catch (error) {
                status = 'rejected'
                httpStatus = 500
            }

            setstate((prevState: any) => ({
                ...prevState, status,
                httpStatus,
                data: {
                    ...prevState.data,
                    events: events
                },
            }));
        }

        fetchLogXplorerEvents()
    }, [])

    const possibleOptions = async (inputValue: string): Promise<[]> => {
        try {
            const searchRoute = L0GXP.FILTER + `?query=${inputValue}`
            const response: any = await HttpServices.httpGet(searchRoute)
            const httpStatus = response.status

            if (httpStatus === 200) {
                const payload: any = response.data.payload;

                return payload.users.map((item: any) => ({
                    value: item.acid,
                    label: item.first_name + ' ' + item.last_name,
                }));
            } else {
                return [];
            }
        } catch (error) {
            console.error("Error fetching options:", error);
            return [];
        }
    }

    const loadOptions = (inputValue: string, callback: (options: []) => void) => {
        possibleOptions(inputValue).then(callback);
    }

    const onDatePickerRangeHandler = (date: any) => {
        const maxDate = new Date();
        const minDate = new Date();

        minDate.setDate(maxDate.getDate() - 90);
        let dateFrom = date[0] ? new Date(date[0]) : null;
        let dateTo = date[1] ? new Date(date[1]) : null

        if (dateFrom && dateFrom < minDate) {
            dateFrom = minDate;
            toast.info(`The 'from' date was adjusted to: ${dateFrom}`);
        }

        if (dateTo && dateTo > maxDate) {
            dateTo = maxDate;
            toast.info(`The 'to' date was adjusted to: ${dateTo}`);
        }

        if (dateFrom && dateTo) {
            // Calculate the difference in days
            const diffInTime = dateTo.getTime() - dateFrom.getTime();
            const diffInDays = diffInTime / (1000 * 3600 * 24);

            // If the difference is greater than 30 days, adjust the 'to' date
            if (diffInDays > 30) {
                dateTo = new Date(dateFrom);
                dateTo.setDate(dateFrom.getDate() + 30);

                // Ensure the adjusted 'to' date does not exceed 'maxDate'
                if (dateTo > maxDate) {
                    dateTo = maxDate;
                }

                let toastMessage = (
                    <span className="text-sm">
                        <span className="block">Dates have been adjusted to fit 30 day limit.</span>
                    </span>
                )

                toast.info(toastMessage);
            }
        }

        const newDates = [
            dateFrom,
            dateTo
        ]

        console.log('date From', date[0]);
        console.log('date To', newDates);
        console.log('date To', typeof newDates);

        setstate(prevState => ({
            ...prevState,
            input: {
                ...prevState.input,
                date: newDates,
                from: dateFrom ? dateFrom.toLocaleDateString('en-CA') : prevState.input.from,
                to: dateTo ? dateTo.toLocaleDateString('en-CA') : prevState.input.to,
            }
        }));
    }

    const userAccountsFilter = (e: any) => {
        console.log('Accounts filter: ', e);

        setstate(prevState => ({
            ...prevState,
            input: {
                ...prevState.input,
                users: e
            }
        }));
    }

    const handleCheckboxChange = (e: any, tag: any) => {
        let { posting } = state
        if (posting) return

        setstate((prevState) => {
            let isChecked = e.target.checked

            const updatedEvents = isChecked
                ? [...prevState.input.events, { selected: tag }] // Add the new filter
                : prevState.input.events.filter((filter) => filter.selected !== tag); // Remove the filter

            return {
                ...prevState,
                input: {
                    ...prevState.input,
                    events: updatedEvents,
                },
            };
        });
    }

    const onFormSubmitHandler = (e: any) => {
        e.preventDefault()
        let { posting, input } = state
        if (posting) return

        const { users, events, from, to } = input

        // Check if no parameters are passed
        if (!to && !from && (!events || events.length === 0) && (!users || users.length === 0)) {
            toast.error("Please set your search parameters");
            return;
        }

        const usersQuery = users?.map((u: any) => u.value).join(',');
        const eventsQuery = events?.map((ev: any) => ev.selected).join(',');

        const query = new URLSearchParams({
            ...(usersQuery && { users: usersQuery }),
            ...(eventsQuery && { events: eventsQuery }),
            ...(from && { from }),
            ...(to && { to })
        }).toString();

        const logXplorerResultsRoute = getStandardRoute('L0GXP.RESULTS_')
        const resultsRoute = `${logXplorerResultsRoute}?${query}`

        return navigate(resultsRoute, { replace: false });
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>

            <div className="w-full h-full flex flex-col items-center justify-center relative bg-gradient-to-b from-white dark:from-slate-800/60 via-[#ffffff] dark:via-slate-700 lg:via-[50%] via-[65%] dark:via-[70%] to-gradient dark:to-orange-700/80">
                <section className=" lg:px-2 md:px-4 px-5 w-full md:w-[500px] mx-auto py-3border-0 rounded-md absolute transform -translate-y-16">
                    {
                        state.status === 'rejected' ? (
                            <>

                            </>
                        ) : state.status === 'fulfilled' ? (
                            <form className="w-full" onSubmit={onFormSubmitHandler}>
                                <header className="landing-header md:pb-3">
                                    <div className="landing mb-0 text-left flex flex-col gap-y-2">
                                        <h2 className={`odyssey text-left text-muted text-base md:text-xl dark:text-slate-300`}>{pageTitle}</h2>
                                    </div>
                                </header>

                                <div className="header-title align-middle flex flex-nowrap title-transition">
                                    <h1 className="title md:text-2xl text-lg tracking-wider leading-9 md:pb-4 pb-3 bg-gradient-to-r from-orange-400 to-red-600 bg-clip-text text-transparent">
                                        Explore. Investigate. Discover.
                                    </h1>
                                </div>

                                <span className="block text-sm">

                                </span>

                                <Theme className={`${isDarkMode ? 'rounded-md shadow-md px-3' : null} pt-4 pb-3 w-full`} theme={isDarkMode ? 'g100' : null}>
                                    <div className={`flex md:flex-row ${isDarkMode ? 'flex-col' : null} items-end align-middle md:gap-x-3 gap-x-1.5 gap-y-4`}>
                                        <DatePicker locale={'en'} className="w-full md:basis-1/2 rounded-md flex md:gap-x-4 dark:text-slate-200" datePickerType="range" value={state.input.date}
                                            onChange={(e) => onDatePickerRangeHandler(e)}
                                            minDate={minDate}
                                            maxDate={currentDate}
                                        >
                                            <DatePickerInput id="date-picker-input-id-start" labelText="Start date" onChange={() => { }} placeholder="mm/dd/yyyy" size="md" />
                                            <DatePickerInput id="date-picker-input-id-finish" labelText="End date" onChange={() => { }} placeholder="mm/dd/yyyy" size="md" />
                                        </DatePicker>

                                        <div className="md:basis-1/2 w-full dark:w-1/2 dark:mt-1 dark:md:mt-0 flex-1">
                                            <DropDown
                                                type="custom"
                                                button={
                                                    <>
                                                        <MenuButton className={`relative bg-slate-200 dark:bg-slate-800 rounded-md w-full h-10 px-3 cursor-default focus:outline-none sm:text-sm flex items-center align-middle gap-x-2 justify-center`}>
                                                            <span className="fa-light fa-sliders float-end fa-lg"></span>

                                                            <div className={`flex-grow ${isDarkMode ? 'md:block' : 'md:block hidden'}`}>
                                                                Events
                                                            </div>
                                                        </MenuButton>
                                                    </>
                                                }
                                                items={
                                                    <div className="text-sm w-64 md:h-64 flex flex-col">
                                                        <div className="w-full px-4 flex-none">
                                                            <div className="w-full border-b border-slate-300 dark:border-slate-600 pt-2">
                                                                <p className="text-sm text-slate-900 dark:text-slate-100 font-medium">
                                                                    Filter by:
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="flex-grow overflow-auto py-3">
                                                            {
                                                                state.data.events.map((event: any) => {
                                                                    let { input } = state
                                                                    const existsInFilter = input.events.some(filter => filter.selected === event.k);

                                                                    return (
                                                                        <>
                                                                            <div key={event.tag} className="w-full text-start px-4 py-2 text-sm dark:text-slate-300">
                                                                                <label className="inline-flex items-center" htmlFor={`ctgy-${event.k}`}>
                                                                                    <input id={`evnt-${event.k}`} type="checkbox" className="w-4 h-4 accent-orange-600 rounded-md" checked={existsInFilter} name="event" onChange={(e) => handleCheckboxChange(e, event.k)} />
                                                                                    <span className="ml-3">{event.e}</span>
                                                                                </label>
                                                                            </div>
                                                                        </>
                                                                    )
                                                                }
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className="pt-4 pb-4 w-full">
                                        <label className="block text-sm leading-6 text-primary-dark dark:text-stone-400 mb-1">
                                            Filter for specific users:
                                        </label>

                                        <AsyncSelect isMulti cacheOptions loadOptions={loadOptions} defaultOptions
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            onChange={(e) => userAccountsFilter(e)}
                                            theme={(theme) => ({
                                                ...theme,
                                                colors: {
                                                    ...theme.colors,
                                                    neutral0: isDarkMode ? "#1F2937" : "#ffffff",
                                                    neutral80: isDarkMode ? "#E5E7EB" : "#1F2937",
                                                    neutral30: isDarkMode ? "#6B7280" : "#D1D5DB",
                                                    primary25: isDarkMode ? "#374151" : "#f3f4f6",
                                                    primary: isDarkMode ? "#1F2937" : "#2563eb",
                                                },
                                            })}
                                        />
                                    </div>

                                    <div className="flex flex-row-reverse pt-2 pb-3">
                                        <button type="submit" className="bg-primary relative flex justify-center py-1.5 px-3 border border-transparent text-sm rounded-md text-white hover:bg-secondary focus:outline-none focus:ring-0 focus:ring-offset-2 focus:bg-secondary">
                                            Search Logs
                                        </button>
                                    </div>
                                </Theme>
                            </form>
                        ) : (
                            <div className="py-2 px-6 w-full mx-auto h-full flex flex-col items-center justify-center align-middle">
                                <LoadingXL />
                            </div>
                        )
                    }
                </section>
            </div>
        </React.Fragment >
    )
}